import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, KeyRound, Send, CheckCircle2, XCircle } from "lucide-react";

export default function Integrations() {
  const [s, setS] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/integrations/email").then(({ data }) => setS(data));
  useEffect(() => { load(); }, []);

  if (!s) return null;

  const save = async (updates) => {
    try {
      const { data } = await api.put("/integrations/email", { ...updates, api_key: apiKey || undefined });
      setS(data);
      if (apiKey) setApiKey("");
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a recipient email");
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/email/test", { to: testTo });
      toast.success(`Test sent · id ${data.id?.slice(0, 10)}…`);
      setTestOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setTesting(false); }
  };

  const StatusBadge = ({ ok, label }) => (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-brand uppercase tracking-widest ${ok ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-red-500/10 text-red-300 border border-red-500/30"}`}>
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {label}
    </span>
  );

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Integrations"
        subtitle="Third-party services BT Control Room talks to"
      />
      <div className="p-8">
        <Tabs defaultValue="email" className="max-w-3xl">
          <TabsList className="rounded-none bg-card border border-border">
            <TabsTrigger value="email" data-testid="tab-email" className="rounded-none data-[state=active]:bg-secondary">
              <Mail className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} /> Email notifications
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="mt-4">
            <Card className="rounded-none border-border p-6 space-y-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg">Resend</div>
                  <div className="text-sm text-muted-foreground">Sends late-arrival nudges and missing punch-out alerts.</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge ok={s.api_key_present} label={s.api_key_present ? "Key set" : "No key"} />
                  <StatusBadge ok={s.enabled} label={s.enabled ? "Enabled" : "Disabled"} />
                </div>
              </div>

              <div className="grid gap-4">
                <div>
                  <Label className="label-mono">From address</Label>
                  <Input
                    data-testid="email-from-input"
                    value={s.from_email}
                    onChange={(e) => setS({ ...s, from_email: e.target.value })}
                    onBlur={() => save({ from_email: s.from_email })}
                    className="rounded-none font-mono-brand"
                  />
                  <div className="label-mono mt-1">Use a domain you've verified inside Resend for best deliverability.</div>
                </div>

                <div>
                  <Label className="label-mono">API key</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      data-testid="email-api-key-input"
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={s.api_key_masked || "re_xxxxxxxxxxxxxxxxx"}
                      className="rounded-none font-mono-brand"
                    />
                    <Button data-testid="email-save-key-btn" onClick={() => save({})} className="rounded-none btn-primary-brand" disabled={!apiKey}>
                      <KeyRound className="h-4 w-4 mr-1" strokeWidth={1.5} /> Save key
                    </Button>
                  </div>
                  <div className="label-mono mt-1">
                    Current: <span className="text-muted-foreground">{s.api_key_masked || "not set"}</span>. Get keys at resend.com/api-keys.
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 pt-2 border-t border-border">
                  <label className="flex items-center justify-between gap-3 border border-border p-3">
                    <div>
                      <div className="text-sm font-medium">Master switch</div>
                      <div className="label-mono">Turn all email off</div>
                    </div>
                    <Switch
                      data-testid="email-enabled-switch"
                      checked={s.enabled}
                      onCheckedChange={(v) => save({ enabled: v })}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-3 border border-border p-3">
                    <div>
                      <div className="text-sm font-medium">Late nudges</div>
                      <div className="label-mono">Morning reminders</div>
                    </div>
                    <Switch
                      data-testid="email-late-switch"
                      checked={s.late_reminders}
                      onCheckedChange={(v) => save({ late_reminders: v })}
                    />
                  </label>
                  <label className="flex items-center justify-between gap-3 border border-border p-3">
                    <div>
                      <div className="text-sm font-medium">Missing punch-out</div>
                      <div className="label-mono">11:59 pm local</div>
                    </div>
                    <Switch
                      data-testid="email-missing-switch"
                      checked={s.missing_punchout_alerts}
                      onCheckedChange={(v) => save({ missing_punchout_alerts: v })}
                    />
                  </label>
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    data-testid="send-test-email-btn"
                    onClick={() => setTestOpen(true)}
                    className="rounded-none btn-primary-brand"
                    disabled={!s.api_key_present || !s.enabled}
                  >
                    <Send className="h-4 w-4 mr-2" strokeWidth={1.5} /> Send test email
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="rounded-none bg-card border-border">
          <DialogHeader><DialogTitle className="font-display">Send test email</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="label-mono">Recipient</Label>
            <Input
              data-testid="test-email-input"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@yourdomain.com"
              className="rounded-none"
            />
            <div className="label-mono">In Resend testing mode this must be a verified inbox.</div>
          </div>
          <DialogFooter>
            <Button data-testid="test-email-send-btn" onClick={sendTest} disabled={testing} className="rounded-none btn-primary-brand">
              {testing ? "Sending…" : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
